import type {
  SharedServiceusersv1User,
  UserApiBatchGetUsersResponse,
} from '@blk/aladdin-graph-client-axios-internal/wealth/shared_service/users/v1/user_api';
import {
  AgraphwealthsharedServiceusersv1BatchUpdateUserEntitlementsRequest,
  AgraphwealthsharedServiceusersv1BatchUpdateUserEntitlementsResponse,
} from '@blk/aladdin-graph-client-axios-internal/wealth/shared_service/users/v1/user_entitlement_api';
import { AxiosResponse, CancelTokenSource } from 'axios';
import { isAGraphEnabled } from './AGraphUtils';
import { awEntitlementsApi } from './Base';

interface SearchUsersRequest {
  pkgScopeSet: string;
  searchTerm: string;
}

//TODO AWM-10456 :: Delete me, hook up aw-entitletments to typescript generator
export interface ClientUser {
  completeViewAccess: boolean;
  email: string;
  firstName: string;
  lastName: string;
  login: string;
  pkg: string[];
}

export interface SharedResourceUserIdsRequest {
  pkg: string;
  resourceIds: string[];
}

export interface SharedResourceUserIdsResponse {
  resourceUsers: {
    [resourceId: string]: {
      userIds: string[];
    };
  };
}

const emptyUser = {
  completeViewAccess: false,
  email: '',
  firstName: '',
  lastName: '',
  login: '',
  pkg: [],
};

export async function getSearchUsers(
  searchUsersRequest: SearchUsersRequest,
  source: CancelTokenSource
): Promise<AxiosResponse<ClientUser[]>> {
  const legacyPath = '/api/v1/users/_search';
  const agraphPath = '/v1/users:search';
  if (isAGraphEnabled()) {
    const config = {
      searchTerm: searchUsersRequest.searchTerm,
      pkgs: [searchUsersRequest.pkgScopeSet],
      cancelToken: source.token,
    };
    const resp = await awEntitlementsApi.post<UserApiBatchGetUsersResponse>(agraphPath, config);
    return {
      ...resp,
      data: resp.data.users?.map((user) => ({
        email: user.userProfile?.email || '',
        firstName: user.userProfile?.firstName || '',
        lastName: user.userProfile?.lastName || '',
        login: user.id,
        completeViewAccess: user.completeViewAccess,
        pkg: user.riskMonitorViewPkgs,
      })) || [emptyUser],
    };
  }
  const config = { params: searchUsersRequest, cancelToken: source.token };
  return awEntitlementsApi.get<ClientUser[]>(legacyPath, config);
}

export const userInfoLegacyPath = '/api/v1/users/info';
export const userInfoAgraphPath = '/v1/users/info';
export async function getUserInfo(): Promise<AxiosResponse<ClientUser>> {
  if (isAGraphEnabled()) {
    const resp = await awEntitlementsApi.get<SharedServiceusersv1User>(userInfoAgraphPath);
    return {
      ...resp,
      data: {
        email: resp.data.userProfile?.email || '',
        firstName: resp.data.userProfile?.firstName || '',
        lastName: resp.data.userProfile?.lastName || '',
        login: resp.data.id,
        completeViewAccess: resp.data.completeViewAccess,
        pkg: resp.data.riskMonitorViewPkgs,
      },
    };
  }
  return awEntitlementsApi.get<ClientUser>(userInfoLegacyPath);
}

export async function getUsersByIDs(logins: string, pkg: string): Promise<AxiosResponse<ClientUser[]>> {
  if (isAGraphEnabled()) {
    const path = '/v1/users:batchGet';
    const resp = await awEntitlementsApi.get<UserApiBatchGetUsersResponse>(path, {
      params: {
        ids: logins.split(','), //logins contain single or comma separated user ids
        pkg,
      },
      paramsSerializer: {
        indexes: null,
      },
    });
    return {
      ...resp,
      data: resp.data.users?.map((user) => ({
        email: user.userProfile?.email || '',
        firstName: user.userProfile?.firstName || '',
        lastName: user.userProfile?.lastName || '',
        login: user.id,
        completeViewAccess: user.completeViewAccess,
        pkg: user.riskMonitorViewPkgs,
      })) || [emptyUser],
    };
  }
  const legacyPath = '/api/v1/users/_resolve';
  return awEntitlementsApi.get<ClientUser[]>(legacyPath, {
    params: { logins, pkgScopeSet: pkg },
  });
}

export async function postSharedResourceUsers(
  data: SharedResourceUserIdsRequest
): Promise<AxiosResponse<SharedResourceUserIdsResponse>> {
  if (isAGraphEnabled()) {
    const path = '/v1/entitlements:sharedResourceUsers';
    return awEntitlementsApi.get<SharedResourceUserIdsResponse>(path, {
      params: { resourceIds: data.resourceIds?.join(','), pkg: data.pkg },
    });
  }
  const path = '/api/v2/entitlements/_sharedResourceUsers';
  const res = await awEntitlementsApi.post<{ resourceIdToUserIds: { [resourceId: string]: string[] } }>(path, data);

  // Transform the response structure
  const resourceUsers: SharedResourceUserIdsResponse['resourceUsers'] = {};
  Object.entries(res.data.resourceIdToUserIds).forEach(([resourceId, userIds]) => {
    resourceUsers[resourceId] = { userIds };
  });

  return {
    ...res,
    data: {
      resourceUsers,
    },
  };
}

export async function postBatchUpdateEntitlements(
  data: AgraphwealthsharedServiceusersv1BatchUpdateUserEntitlementsRequest
): Promise<AxiosResponse<AgraphwealthsharedServiceusersv1BatchUpdateUserEntitlementsResponse>> {
  const path = '/v1/entitlements:batchUpdate';
  return awEntitlementsApi.post<AgraphwealthsharedServiceusersv1BatchUpdateUserEntitlementsResponse>(path, data);
}
